import React, { ReactNode, useState } from 'react'
import {
  BottomDrawer,
  breakpointQueries,
  Button,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { ParseKeys } from 'i18next'
import { useTranslation } from 'next-i18next'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import { BadRequest } from '../errors/bad-request'
import { InternalError } from '../errors/internal-error'

type ConfirmationProps = {
  onCloseModal: () => void
  onConfirm: () => void
  title?: ParseKeys
  confirmationContent?: React.ReactNode
  errorMessageMap?: Partial<Record<'internalError' | 'unprocessableContentError', ParseKeys>>
}

function Confirmation({
  onCloseModal,
  onConfirm,
  title = 'confirmation_modal.default_title',
  confirmationContent = '',
  errorMessageMap,
}: ConfirmationProps): ReactNode {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(true)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  function closeModal(): void {
    setOpened(false)
    onCloseModal()
  }

  const handleConfirm = async (): Promise<void> => {
    try {
      setIsLoading(true)
      await onConfirm()
      setOpened(false)
    } catch (error_) {
      if (error_ instanceof BadRequest && error_.errors.common) {
        setError(error_.errors.common.join(''))
      } else if (
        error_ instanceof UnprocessableContentError &&
        errorMessageMap?.unprocessableContentError
      ) {
        setError(t(errorMessageMap.unprocessableContentError))
      } else if (error_ instanceof InternalError) {
        setError(t(errorMessageMap?.internalError ?? 'core.error.internal_error_message'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isMobile) {
    return (
      <Modal.Root
        open={opened}
        onOpenChange={open => {
          if (!open) closeModal()
        }}
      >
        <Modal.Popup>
          <Modal.Header>
            <Modal.Title>{t(title)}</Modal.Title>
          </Modal.Header>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
            <div className="flex justify-center">{confirmationContent}</div>
            {error && <p className="text-center text-sm text-text-error">{error}</p>}
          </div>
          <Modal.Footer>
            <Button variant="secondary" onClick={onCloseModal}>
              {t('confirmation_modal.cancel')}
            </Button>
            <Button variant="primary" isLoading={isLoading} onClick={handleConfirm}>
              {t('confirmation_modal.confirm')}
            </Button>
          </Modal.Footer>
        </Modal.Popup>
      </Modal.Root>
    )
  }

  return (
    <BottomDrawer.Root
      open={opened}
      onOpenChange={open => {
        if (!open) closeModal()
      }}
    >
      <BottomDrawer.Popup>
        <BottomDrawer.Header className="flex-row items-start gap-2.5">
          <BottomDrawer.Title className="min-w-0 flex-1">{t(title)}</BottomDrawer.Title>
          <BottomDrawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </BottomDrawer.Close>
        </BottomDrawer.Header>
        <BottomDrawer.Content>
          <div className="flex justify-center">{confirmationContent}</div>
          {error && <p className="text-center text-sm text-text-error">{error}</p>}
        </BottomDrawer.Content>
        <BottomDrawer.Footer>
          <Button variant="secondary" onClick={onCloseModal}>
            {t('confirmation_modal.cancel')}
          </Button>
          <Button variant="primary" isLoading={isLoading} onClick={handleConfirm}>
            {t('confirmation_modal.confirm')}
          </Button>
        </BottomDrawer.Footer>
      </BottomDrawer.Popup>
    </BottomDrawer.Root>
  )
}

export default Confirmation
